import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Database, AlertTriangle, FileCode2, Boxes, Layers, ListTree, ShieldCheck, Shapes, Workflow, Image as ImageIcon, Palette, Sprout } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { AdminTabs } from '../shared/AdminTabs';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Card } from '../../../components/ui/Card';
import { getSsotRegistry, SSOT_REGISTRY_COUNT } from '../../../governance';
import type { SsotEntry, SsotDomain, SsotSnapshot } from '../../../governance';
// ─── Domain presentation config ─────────────────────────────────────────────
const DOMAIN_ORDER: SsotDomain[] = ['taxonomy', 'cognitive', 'governance', 'pipeline', 'content', 'visuals', 'mastery', 'design'];
const DOMAIN_CONFIG: Record<SsotDomain, {
  label: string;
  icon: React.ReactNode;
  badge: string;
  dot: string;
  description: string;
}> = {
  taxonomy: {
    label: 'Taxonomie',
    icon: <ListTree className="w-4 h-4" data-id="element-7153" />,
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    dot: 'bg-indigo-500',
    description: 'Tiers en de canonieke leerstof-strook.'
  },
  cognitive: {
    label: 'Cognitief',
    icon: <Layers className="w-4 h-4" data-id="element-7154" />,
    badge: 'bg-violet-50 text-violet-700 border-violet-200',
    dot: 'bg-violet-500',
    description: 'DOK-floors/ceilings, cognitieve profielen en bounds.'
  },
  governance: {
    label: 'Governance',
    icon: <ShieldCheck className="w-4 h-4" data-id="element-7155" />,
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    description: 'Vraagtype-metadata die validatie en gating stuurt.'
  },
  pipeline: {
    label: 'Pipeline',
    icon: <Workflow className="w-4 h-4" data-id="element-7156" />,
    badge: 'bg-rose-50 text-rose-700 border-rose-200',
    dot: 'bg-rose-500',
    description: 'Stages, issue-codes, severity-ladder en scoring-gewichten.'
  },
  visuals: {
    label: 'Visuals',
    icon: <ImageIcon className="w-4 h-4" data-id="element-7157" />,
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
    description: 'Visual-rollen per vraagtype.'
  },
  content: {
    label: 'Content',
    icon: <Shapes className="w-4 h-4" data-id="element-7158" />,
    badge: 'bg-sky-50 text-sky-700 border-sky-200',
    dot: 'bg-sky-500',
    description: 'Content-structuur SSOTs.'
  },
  mastery: {
    label: 'Mastery',
    icon: <Sprout className="w-4 h-4" data-id="element-7159" />,
    badge: 'bg-teal-50 text-teal-700 border-teal-200',
    dot: 'bg-teal-500',
    description: 'Adaptieve-engine parameters, groeistadia en difficulty-proxycontract.'
  },
  design: {
    label: 'Design System',
    icon: <Palette className="w-4 h-4" data-id="element-7160" />,
    badge: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
    dot: 'bg-fuchsia-500',
    description: 'Module-palet, status-/rol-kleuren, mastery- en radius-schaal.'
  }
};
/**
 * SsotRegistryPage — dedicated, top-level admin surface for every catalogued
 * single-source-of-truth. Each domain gets its own tab; within a tab every
 * SSOT is rendered with its metadata and a LIVE values table (read at render
 * time via `entry.read()`). Drift notes surface as amber cautions. The whole
 * surface is read-only and fully data-driven — new registry entries appear
 * automatically with zero changes here.
 */
export function SsotRegistryPage() {
  const entries = useMemo(() => getSsotRegistry(), []);
  const grouped = useMemo(() => {
    const map = new Map<SsotDomain, SsotEntry[]>();
    entries.forEach(entry => {
      const list = map.get(entry.domain) ?? [];
      list.push(entry);
      map.set(entry.domain, list);
    });
    return map;
  }, [entries]);
  // Domains that actually carry entries, in canonical order, with any unknown
  // domains appended so nothing is ever silently hidden.
  const domains = useMemo(() => {
    const present = Array.from(grouped.keys());
    const ordered = DOMAIN_ORDER.filter(d => present.includes(d));
    const extras = present.filter(d => !DOMAIN_ORDER.includes(d));
    return [...ordered, ...extras];
  }, [grouped]);
  const notesCount = useMemo(() => entries.filter(e => e.notes).length, [entries]);
  const [activeTab, setActiveTab] = useState<SsotDomain | ''>(() => domains[0] ?? '');
  const tabs = useMemo(() => domains.map(domain => {
    const cfg = DOMAIN_CONFIG[domain];
    return {
      id: domain,
      label: cfg?.label ?? domain,
      icon: cfg?.icon ?? <Boxes className="w-4 h-4" data-id="element-7161" />
    };
  }), [domains]);
  const activeDomain = (activeTab || domains[0]) as SsotDomain | undefined;
  const activeEntries = activeDomain ? grouped.get(activeDomain) ?? [] : [];
  const activeCfg = activeDomain ? DOMAIN_CONFIG[activeDomain] : undefined;
  return <PageLayout title="SSOT Registry" subtitle="Alle single-sources-of-truth — data-driven en live uit de code gelezen" actions={<ConnectionBadge status="live" data-id="element-7163" />} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'SSOT Registry'
  }]} data-id="element-7162">
      {/* ── Summary header ── */}
      <Card className="p-5 mb-5" data-id="element-7164">
        <div className="flex items-start gap-4" data-id="element-7165">
          <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0" data-id="element-7166">
            <Database className="w-5 h-5" data-id="element-7167" />
          </div>
          <div className="min-w-0" data-id="element-7168">
            <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-7169">
              Single-Sources-of-Truth
            </h2>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed" data-id="element-7170">
              <span className="font-semibold text-gray-900 tabular-nums" data-id="element-7171">
                {SSOT_REGISTRY_COUNT}
              </span>{' '}
              gecatalogiseerde single-sources-of-truth, verdeeld over{' '}
              <span className="font-semibold text-gray-900 tabular-nums" data-id="element-7172">
                {domains.length}
              </span>{' '}
              {domains.length === 1 ? 'domein' : 'domeinen'}. Elke tabel toont
              de actuele waarden zoals de draaiende app ze nu hanteert.
              {notesCount > 0 && <>
                  {' '}
                  <span className="text-amber-700 font-medium" data-id="element-7173">
                    {notesCount} {notesCount === 1 ? 'entry' : 'entries'}
                  </span>{' '}
                  met een bekende drift-notitie.
                </>}
            </p>
          </div>
        </div>
      </Card>

      {/* ── Domain tabs ── */}
      {tabs.length > 0 && <AdminTabs tabs={tabs} activeTab={activeDomain ?? ''} onTabChange={id => setActiveTab(id as SsotDomain)} data-id="element-7174" />}

      {activeCfg?.description && <p className="text-sm text-gray-500 mb-5" data-id="element-7175">{activeCfg.description}</p>}

      {/* ── Active domain entries ── */}
      <div role="tabpanel" id={`tabpanel-${activeDomain ?? 'none'}`} className="space-y-4" data-id="element-7176">
        {activeEntries.length === 0 ? <p className="text-sm text-gray-400 italic" data-id="element-7177">
            Geen SSOT-bronnen in dit domein.
          </p> : activeEntries.map((entry, idx) => <SsotEntryCard key={entry.id} entry={entry} dot={activeCfg?.dot ?? 'bg-gray-400'} badge={activeCfg?.badge ?? 'bg-gray-50 text-gray-700 border-gray-200'} domainLabel={activeCfg?.label ?? activeDomain ?? ''} index={idx} data-id="element-7178" />)}
      </div>
    </PageLayout>;
}
// ─── Entry card ───────────────────────────────────────────────────────────
function SsotEntryCard({
  entry,
  dot,
  badge,
  domainLabel,
  index
}: {
  entry: SsotEntry;
  dot: string;
  badge: string;
  domainLabel: string;
  index: number;
}) {
  // LIVE read at render time — never cached, never copied.
  const snapshot = useMemo(() => entry.read(), [entry]);
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.25,
    delay: Math.min(index * 0.04, 0.2)
  }} data-id="element-7179">
      <Card className="p-5" data-id="element-7180">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3" data-id="element-7181">
          <div className="min-w-0" data-id="element-7182">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-7183">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} data-id="element-7184" />
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-7185">
                {entry.name}
              </h4>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${badge}`} data-id="element-7186">
                {domainLabel}
              </span>
            </div>
            <p className="text-xs text-gray-600 mt-1.5 leading-relaxed" data-id="element-7187">
              {entry.description}
            </p>
          </div>
        </div>

        {/* Metadata row */}
        <div className="flex flex-col gap-2 mb-3 text-[11px]" data-id="element-7188">
          <div className="flex items-center gap-2 text-gray-500" data-id="element-7189">
            <FileCode2 className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" data-id="element-7190" />
            <code className="font-mono text-gray-700 break-all" data-id="element-7191">
              {entry.file}
            </code>
            <span className="text-gray-300" data-id="element-7192">·</span>
            <code className="font-mono text-gray-700 break-all" data-id="element-7193">
              {entry.exportName}
            </code>
          </div>
          <p className="text-gray-500 leading-relaxed" data-id="element-7194">
            <span className="font-medium text-gray-700" data-id="element-7195">Bepaalt:</span>{' '}
            {entry.governs}
          </p>
          {entry.consumers.length > 0 && <div className="flex items-start gap-2 flex-wrap" data-id="element-7196">
              <span className="font-medium text-gray-700 pt-0.5" data-id="element-7197">
                Consumers:
              </span>
              <div className="flex flex-wrap gap-1" data-id="element-7198">
                {entry.consumers.map(consumer => <span key={consumer} className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono text-[10px]" data-id="element-7199">
                    {consumer}
                  </span>)}
              </div>
            </div>}
        </div>

        {/* Drift / caution note */}
        {entry.notes && <div role="note" className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 mb-3" data-id="element-7200">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-7201" />
            <p className="text-[11px] text-amber-900 leading-relaxed" data-id="element-7202">
              {entry.notes}
            </p>
          </div>}

        {/* Live snapshot table */}
        <SnapshotTable snapshot={snapshot} data-id="element-7203" />
      </Card>
    </motion.div>;
}
// ─── Snapshot table ─────────────────────────────────────────────────────────
function SnapshotTable({
  snapshot
}: {
  snapshot: SsotSnapshot;
}) {
  if (snapshot.rows.length === 0) {
    return <p className="text-[11px] text-gray-400 italic px-1 py-2" data-id="element-7204">
        Geen waarden om te tonen.
      </p>;
  }
  return <div className="overflow-x-auto -mx-1 px-1 rounded-lg border border-gray-100" data-id="element-7205">
      <table className="min-w-full text-[11px]" data-id="element-7206">
        <thead data-id="element-7207">
          <tr className="bg-gray-50" data-id="element-7208">
            {snapshot.columns.map(col => <th key={col.key} className="text-left font-semibold text-gray-600 uppercase tracking-wider px-3 py-2 whitespace-nowrap border-b border-gray-100" data-id="element-7209">
                {col.label}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-7210">
          {snapshot.rows.map((row, rowIdx) => <tr key={rowIdx} className={rowIdx % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'} data-id="element-7211">
              {snapshot.columns.map(col => {
            const value = row[col.key];
            return <td key={col.key} className="px-3 py-1.5 text-gray-700 tabular-nums whitespace-nowrap border-b border-gray-50 font-mono" data-id="element-7212">
                    {value === undefined || value === '' ? <span className="text-gray-300" data-id="element-7213">—</span> : value}
                  </td>;
          })}
            </tr>)}
        </tbody>
      </table>
    </div>;
}